import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Card,
  Link,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import ProgressTracker from "./ProgressTracker.component";
import ReformationReviewSection from "./ReformationReviewSection.component";
import FAQDrawer from "./FAQDrawer.component";
import TraderQuestion from "./TraderQuestion.component";

// Redux imports
import {
  formAutoSaveRequest,
  updateFormData,
  formDataSubmit,
  addTraderSectionRequest,
  deleteTraderSectionRequest,
  deleteFileRequest,
} from "../reducers/form.reducer";
import { setFileUploadStatus } from "../reducers/misc.reducer";
import {
  dataUpdate,
  sectionNoteSaveStart,
  sectionNoteWithdrawStart,
} from "../reducers/collaborativeForm.reducer";
import {
  selectFormData,
  selectAutoSaveState,
  selectNotesState,
  selectSectionProgressData,
} from "../selectors/form.selector";
import {
  selectSectionSaveState,
  selectFormStatus,
} from "../selectors/collaborativeForm.selector";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import AuditDocumentDrop from "./AuditDetails/auditDocumentDrop.component";
import { generateCompositionFromBasicInfo } from "../utils/lib.js";
import { traderSectionFieldDefinitions } from "../configs/traderSectionFieldDefinitions.js";

// Factory imports for facility picker
import {
  getListOfAllFactories,
  setSelectedFactoryFromPicker,
} from "../reducers/factory.reducer";
import {
  selectFactoryList,
  selectSelectedFactoryFromPicker,
} from "../selectors/factory.selector";
import PrefillFactoryInformationPicker from "./prefillFactoryInformationPicker.component";

// Selector for upload states
const selectUploadStates = (state) =>
  state.collaborativeForm?.uploadStates || {};


// Dynamic styled components based on JSON styling config
const createStyledTextField = (styling) =>
  styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: styling.backgroundColor,
      borderRadius: styling.borderRadius,
      "& fieldset": {
        borderColor: styling.borderColor,
      },
      "&:hover fieldset": {
        borderColor: "#c4c4c4",
      },
      "&.Mui-focused fieldset": {
        borderColor: styling.primaryColor,
      },
    },
    "& .MuiInputLabel-root": {
      color: styling.secondaryColor,
      fontSize: styling.fontSize,
      fontWeight: styling.fontWeight,
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      fontSize: styling.fontSize,
    },
  }));

const createStyledFormControl = (styling) =>
  styled(FormControl)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: styling.backgroundColor,
      borderRadius: styling.borderRadius,
      "& fieldset": {
        borderColor: styling.borderColor,
      },
      "&:hover fieldset": {
        borderColor: "#c4c4c4",
      },
      "&.Mui-focused fieldset": {
        borderColor: styling.primaryColor,
      },
    },
    "& .MuiInputLabel-root": {
      color: styling.secondaryColor,
      fontSize: styling.fontSize,
      fontWeight: styling.fontWeight,
    },
    "& .MuiSelect-select": {
      padding: "12px 14px",
      fontSize: styling.fontSize,
    },
  }));

const createStyledButton = (styling, variant = "primary") =>
  styled(Button)(({ theme }) => {
    const isPrimary = variant === "primary";
    return {
      backgroundColor: isPrimary
        ? styling.primaryColor
        : styling.backgroundColor,
      color: isPrimary ? "#ffffff" : styling.secondaryColor,
      border: isPrimary ? "none" : `1px solid ${styling.primaryColor}`,
      textTransform: "none",
      fontWeight: styling.fontWeight,
      padding: "4px 10px",
      borderRadius: "8px",
      fontSize: styling.fontSize,
      "&:hover": {
        backgroundColor: isPrimary ? "#4338ca" : "#f8f9fa",
      },
      "&:disabled": {
        backgroundColor: isPrimary ? styling.primaryColor : styling.backgroundColor,
        color: isPrimary ? "#ffffff" : styling.secondaryColor,
        opacity: 0.6,
      },
    };
  });

const JsonFormRenderer = ({
  formConfig,
  onFormChange,
  formId,
  userId,
  organisationId,
}) => {
  const [localFormData, setLocalFormData] = useState({});
  const [StyledTextField, setStyledTextField] = useState(null);
  const [StyledFormControl, setStyledFormControl] = useState(null);
  const [PrimaryButton, setPrimaryButton] = useState(null);
  const [SecondaryButton, setSecondaryButton] = useState(null);
  const [openFAQDrawer, setOpenFAQDrawer] = useState(false);
  const [currentSectionTitle, setCurrentSectionTitle] = useState("");

  const progressTrackerRef = useRef();

  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadContext, setUploadContext] = useState({
    sectionKey: null,
    fieldId: null,
    sectionId: null,
  });

  // Add state for inline note sections
  const [inlineNoteSections, setInlineNoteSections] = useState({});
  const [noteTexts, setNoteTexts] = useState({});
  const [noteRefs, setNoteRefs] = useState({});

  // Add state for facility picker
  const [facilityPickerOpen, setFacilityPickerOpen] = useState(false);
  const [facilityPickerSectionId, setFacilityPickerSectionId] = useState(null);

  // Add state for conditional logic updates
  const [conditionalLogicUpdate, setConditionalLogicUpdate] = useState(0);
  
  // Add state for real-time conditional field values (local state for immediate updates)
  const [realTimeConditionalValues, setRealTimeConditionalValues] = useState({});
  
  // Add state to track shown alerts to prevent multiple alerts
  const [shownAlerts, setShownAlerts] = useState(new Set());
  
  // FAQ drawer handlers
  const handleOpenCloseFAQDrawer = (drawerState) => {
    setOpenFAQDrawer(drawerState);
  };

  // Function to check incomplete fields for a section
  const checkIncompleteFieldsForSection = (section, sectionData, sectionKey) => {
    const incompleteFields = [];
    
    if (!section.fields) {
      return incompleteFields;
    }

    // Helper to check if a field is required (either directly or conditionally)
    const isFieldRequired = (field) => {
      // Always required
      if (field.required) return true;
      
      // Conditionally required - check if condition is met
      if (field.conditionalRequired && field.conditional) {
        return shouldShowField(field, sectionData, reduxFormData, sectionKey, localFormData);
      }
      
      return false;
    };

    // Process all fields, including expanding certificate sections
    const processField = (field) => {
      // Handle certificate_section fields by checking their additionalInputs
      if (field.type === "certificate_section" && field.additionalInputs) {
        field.additionalInputs.forEach((inputField) => {
          // Check if field is required (either directly or conditionally)
          if (isFieldRequired(inputField)) {
            // Check if field should be shown based on conditional logic
            if (!shouldShowField(inputField, sectionData, reduxFormData, sectionKey, localFormData)) {
              return; // Skip hidden fields
            }

            const value = sectionData[inputField.id];
            let isEmpty = false;

            if (inputField.type === "checkbox" || inputField.type === "multiselect") {
              isEmpty = !Array.isArray(value) || value.length === 0;
            } else if (inputField.type === "file" || inputField.type === "upload") {
              isEmpty = !value || (Array.isArray(value) && value.length === 0);
            } else {
              // For regular fields, check if value is null, undefined, or empty string
              isEmpty = value === null || value === undefined || value === "" || 
                       (typeof value === "string" && value.trim() === "");
            }

            if (isEmpty) {
              incompleteFields.push(inputField.label || inputField.id);
            }
          }
        });
      } else if (field.type === "checkbox" && field.additionalInputs) {
        // Handle checkbox fields with additionalInputs (like cleanChemistry)
        // First check if the main checkbox field should be shown
        const isCheckboxShown = shouldShowField(field, sectionData, reduxFormData, sectionKey, localFormData);
        
        if (!isCheckboxShown) {
          return; // Skip if checkbox itself is hidden
        }
        
        // Check if at least one checkbox option is selected
        const checkboxValue = sectionData[field.id];
        const isCheckboxEmpty = !Array.isArray(checkboxValue) || checkboxValue.length === 0;
        
        if (isFieldRequired(field) && isCheckboxEmpty) {
          // Check if field should be shown based on conditional logic
          if (!shouldShowField(field, sectionData, reduxFormData, sectionKey, localFormData)) {
            // Field is hidden, skip
          } else {
            incompleteFields.push(field.label || field.id);
          }
        }
        
        // Also check the additionalInputs fields when checkbox section is visible
        // If the parent checkbox field is conditionally required and visible, 
        // treat its additionalInputs as required too
        if (field.additionalInputs && field.additionalInputs.length > 0) {
          const parentIsConditionallyRequired = field.conditionalRequired && isCheckboxShown;
          
          field.additionalInputs.forEach((addField) => {
            // Check if this additional field is required
            // Either explicitly required, or implicitly required because parent is conditionally required
            const addFieldIsRequired = isFieldRequired(addField) || parentIsConditionallyRequired;
            
            if (addFieldIsRequired) {
              // Check if field should be shown based on its own conditional logic
              if (!shouldShowField(addField, sectionData, reduxFormData, sectionKey, localFormData)) {
                return; // Skip hidden fields
              }

              const value = sectionData[addField.id];
              let isEmpty = false;

              if (addField.type === "checkbox" || addField.type === "multiselect") {
                isEmpty = !Array.isArray(value) || value.length === 0;
              } else if (addField.type === "file" || addField.type === "upload") {
                isEmpty = !value || (Array.isArray(value) && value.length === 0);
              } else {
                isEmpty = value === null || value === undefined || value === "" || 
                         (typeof value === "string" && value.trim() === "");
              }

              if (isEmpty) {
                incompleteFields.push(addField.label || addField.id);
              }
            }
          });
        }
      } else if (isFieldRequired(field)) {
        // Regular field check - now handles both required and conditionalRequired
        // Check if field should be shown based on conditional logic
        if (!shouldShowField(field, sectionData, reduxFormData, sectionKey, localFormData)) {
          return; // Skip hidden fields
        }

        const value = sectionData[field.id];
        let isEmpty = false;

        if (field.type === "checkbox" || field.type === "multiselect") {
          isEmpty = !Array.isArray(value) || value.length === 0;
        } else if (field.type === "file" || field.type === "upload") {
          isEmpty = !value || (Array.isArray(value) && value.length === 0);
        } else {
          // For regular fields, check if value is null, undefined, or empty string
          isEmpty = value === null || value === undefined || value === "" || 
                   (typeof value === "string" && value.trim() === "");
        }

        if (isEmpty) {
          incompleteFields.push(field.label || field.id);
        }
      }
    };

    // Process all fields
    section.fields.forEach(processField);

    return incompleteFields;
  };

  const handleOpen = (sectionKey, fieldId, sectionId, type = null, option = null) => {
    const uniqueKey = sectionKey.split("_key_")[1];

    setUploadContext({
      sectionKey,
      fieldId,
      sectionId,
      uniqueKey,
      formId,
      type,
      option, // Add option information for clean chemistry uploads
    });
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
    }, 2000);
  };

  // Handle opening inline note section
  const handleOpenInlineNote = (sectionId) => {
    setInlineNoteSections((prev) => ({
      ...prev,
      [sectionId]: true,
    }));
    setNoteTexts((prev) => ({
      ...prev,
      [sectionId]: {
        poResponse: prev[sectionId]?.poResponse || "",
        note: prev[sectionId]?.note || "",
      },
    }));

    // Scroll to the note section after it appears
    setTimeout(() => {
      const noteElement = document.getElementById(`note-section-${sectionId}`);
      if (noteElement) {
        noteElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  // Handle note submission
  const handleNoteSubmit = (sectionId) => {
    const noteData = noteTexts[sectionId];
    const note = noteData?.note?.trim();
    const poResponse = noteData?.poResponse;

    if (note) {

      // Get the uniqueKey from sectionKeyMapping
      const sectionKeyMapping = formConfig?.sectionKeyMapping || {};
      const uniqueKey = sectionKeyMapping[sectionId];

      if (!uniqueKey) {
        return;
      }

      const actionPayload = {
        formId,
        userId,
        organisationId,
        sectionId,
        sectionKey: uniqueKey,
        note,
        poResponse,
        timestamp: new Date().toISOString(),
      };

      // Dispatch action to save the note via Redux
      dispatch(sectionNoteSaveStart(actionPayload));

      // Note: Keep the inline note section open after submission
      // The section will remain visible to show the submitted note
    }
  };

  // Handle canceling/closing note section
  const handleCancelNote = (sectionId) => {
    setInlineNoteSections((prev) => ({
      ...prev,
      [sectionId]: false,
    }));
    // Only clear note texts if note hasn't been submitted yet
    const sectionStatus = (() => {
      const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
      const currentSection = sectionsArray.find(
        (s) => (s.id || s.sectionId) === sectionId
      );
      return currentSection?.status?.toLowerCase();
    })();

    // If note is not submitted, clear the texts
    if (sectionStatus !== "submitted_with_note") {
      setNoteTexts((prev) => ({
        ...prev,
        [sectionId]: {
          poResponse: "",
          note: "",
        },
      }));
    }
  };

  // Handle withdrawing note (remove submitted note and reset status)
  const handleWithdrawNote = (sectionId) => {
    // Get the uniqueKey from sectionKeyMapping
    const sectionKeyMapping = formConfig?.sectionKeyMapping || {};
    const uniqueKey = sectionKeyMapping[sectionId];

    if (!uniqueKey) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to withdraw the request to submit with note? This will remove the note and reset the section status."
    );

    if (!confirmed) {
      return;
    }

    const actionPayload = {
      formId,
      userId,
      organisationId,
      sectionId,
      sectionKey: uniqueKey,
    };

    // Dispatch action to withdraw the note via Redux
    dispatch(sectionNoteWithdrawStart(actionPayload));

    // Close the note section and clear local state after withdraw
    setInlineNoteSections((prev) => ({
      ...prev,
      [sectionId]: false,
    }));
    setNoteTexts((prev) => ({
      ...prev,
      [sectionId]: {
        poResponse: "",
        note: "",
      },
    }));
  };

  // Handle opening facility picker
  const handleOpenFacilityPicker = (sectionId) => {
    setFacilityPickerSectionId(sectionId);
    setFacilityPickerOpen(true);
  };

  // Handle closing facility picker
  const handleCloseFacilityPicker = () => {
    setFacilityPickerOpen(false);
    setFacilityPickerSectionId(null);
    // Clear the selected factory from Redux state
    dispatch(setSelectedFactoryFromPicker(null));
  };

  // Check conditional logic for sections
  const shouldShowSection = (section, allFormData) => {
    if (!section.conditional) return true;
    
    const { section: conditionalSection, field, condition, value } = section.conditional;
    
    // Find the conditional section's unique key from the form config
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const conditionalSectionObj = sectionsArray.find(s => 
      (s.id || s.sectionId) === conditionalSection
    );
    
    if (!conditionalSectionObj) {
      return true;
    }
    
    const conditionalSectionKey = `form_${formId}_section_${conditionalSection}_key_${conditionalSectionObj.uniqueKey}`;
    const conditionalSectionData = allFormData[conditionalSectionKey] || {};
    
    // Priority 1: Use real-time value if available (for immediate updates)
    // Priority 2: Use Redux form data (for current session)
    // Priority 3: Use database value (for initial load after refresh)
    let fieldValue = realTimeConditionalValues[conditionalSectionKey]?.[field] || 
                     conditionalSectionData[field] || 
                     conditionalSectionObj.data?.[field];
    
    // Additional check: if we have a real-time value, use it immediately
    const hasRealTimeValue = realTimeConditionalValues[conditionalSectionKey]?.[field] !== undefined;
    if (hasRealTimeValue) {
      fieldValue = realTimeConditionalValues[conditionalSectionKey][field];
    }
    
    
    if (condition === 'equals') {
      return fieldValue === value;
    }
    return fieldValue !== value;
  };

  // Handle facility selection and populate form fields
  const handleFacilitySelect = (facility) => {
    // setSelectedFacility(facility); // This state is removed

    // Get the section data for the current section
    const sectionsArray = normalizeSections(formConfig?.sections || {});
    const currentSection = sectionsArray.find(
      (section) => (section.id || section.sectionId) === facilityPickerSectionId
    );

    if (currentSection) {
      const sectionId = currentSection.id || currentSection.sectionId;
      const sectionKey = `form_${formId}_section_${sectionId}_key_${currentSection.uniqueKey}`;

      // Map facility data to form fields based on CreateFactoryModal fields
      const facilityData = {
        name: facility.name || "",
        address: facility?.metadata?.streetAddress || facility?.metadata?.address || facility?.streetAddress || "",
        city: facility?.metadata?.city || facility?.city || "",
        country: facility?.metadata?.country || facility?.country || "",
        parentCompanyName: facility.supplierName || facility?.metadata?.parentCompanyName || "",
        parentCompanyStreet: facility?.metadata?.parentCompanyStreet || "",
        parentCompanyCity: facility?.metadata?.parentCompanyCity || "",
        parentCompanyCountry: facility?.metadata?.parentCompanyCountry || "",
        worldlyId: facility?.metadata?.worldlyId || facility?.metadata?.higgId || "",
        zdhcId: facility?.metadata?.zdhcId || "",
        teId: facility?.metadata?.teId || "",
        osHubId: facility?.metadata?.osHubId || "",   
      };

      // Get ID values for conditional logic
      const worldlyIdValue = facility?.metadata?.worldlyId || facility?.metadata?.higgId || "";
      const zdhcIdValue = facility?.metadata?.zdhcId || "";
      const teIdValue = facility?.metadata?.teId || "";
      const osHubIdValue = facility?.metadata?.osHubId || "";

      // Set "Have ID?" dropdown fields based on whether corresponding ID values exist
      if (worldlyIdValue) {
        facilityData.haveWorldlyId = "Yes";
        facilityData.haveHiggId = "Yes"; // Legacy field mapping
      } else {
        facilityData.haveWorldlyId = "No";
        facilityData.haveHiggId = "No"; // Legacy field mapping
      }
      
      if (zdhcIdValue) {
        facilityData.haveZdhcId = "Yes";
      } else {
        facilityData.haveZdhcId = "No";
      }
      
      if (teIdValue) {
        facilityData.haveTeId = "Yes";
      } else {
        facilityData.haveTeId = "No";
      }
      
      if (osHubIdValue) {
        facilityData.haveOsHubId = "Yes";
      } else {
        facilityData.haveOsHubId = "No";
      }

      // Also set "information on parent company" to "Yes" if any parent company fields have values
      const parentCompanyName = facility.supplierName || facility?.metadata?.parentCompanyName || "";
      const parentCompanyStreet = facility?.metadata?.parentCompanyStreet || "";
      const parentCompanyCity = facility?.metadata?.parentCompanyCity || "";
      const parentCompanyCountry = facility?.metadata?.parentCompanyCountry || "";

      if (parentCompanyName || parentCompanyStreet || parentCompanyCity || parentCompanyCountry) {
        facilityData.informationOnParentCompany = "Yes";
      }

      // First, update all fields in Redux store
      const updatedSectionData = { ...reduxFormData[sectionKey] };

      Object.entries(facilityData).forEach(([fieldId, value]) => {
        // console.log(`[Facility Picker] Setting field ${fieldId} = ${value}`);

        // Check if the field exists in the section
        const fieldExists = currentSection.fields?.some(
          (field) => field.id === fieldId
        );
        if (fieldExists) {
          // Update Redux store directly
          dispatch(
            updateFormData({
              sectionKey,
              fieldId,
              value,
            })
          );

          // Update local state
          setLocalFormData((prev) => ({
            ...prev,
            [sectionKey]: {
              ...prev[sectionKey],
              [fieldId]: value,
            },
          }));

          // Add to updated section data for auto-save
          updatedSectionData[fieldId] = value;
        }
      });

      // Trigger auto-save with complete updated section data after a short delay
      setTimeout(() => {
        debouncedAutoSave(
          sectionId,
          formId,
          userId,
          organisationId,
          updatedSectionData
        );
      }, 100);
    }

    // Close the picker
    handleCloseFacilityPicker();
  };

  const params = useParams();

  // Helper function to normalize sections to array format
  // Handles both old array format and new object format with unique keys
  const normalizeSections = (sections) => {
    if (!sections) return [];

    // If it's already an array, return as-is
    if (Array.isArray(sections)) {
      // console.log(
      //   "JsonFormRenderer: Using array format sections",
      //   sections.length
      // );
      return sections;
    }

    // If it's an object (new format), convert to array
    if (typeof sections === "object") {
      const sectionsArray = Object.values(sections);
      // console.log("JsonFormRenderer: Converted object format to array", {
      //   originalKeys: Object.keys(sections),
      //   sectionsCount: sectionsArray.length,
      // });
      return sectionsArray;
    }

    // console.warn("JsonFormRenderer: Unknown sections format", sections);
    return [];
  };

  // Redux hooks
  const dispatch = useDispatch();
  const reduxFormData = useSelector(selectFormData);
  const autoSaveState = useSelector(selectAutoSaveState);
  const notesState = useSelector(selectNotesState);
  const sectionProgressData = useSelector(selectSectionProgressData);

  // Get submission state
  const submissionState = useSelector((state) => state.form?.submission || {});

  // Get form status from collaborative form
  const formStatus = useSelector(selectFormStatus);

  // Get upload states
  const uploadStates = useSelector(selectUploadStates);
  
  // Clear global file upload status on component mount to prevent cross-contamination
  useEffect(() => {
    // Clear any global file upload status when form loads
    dispatch(setFileUploadStatus(0));
  }, [dispatch]);


  // Factory-related Redux hooks
  const factoriesList = useSelector(selectFactoryList);
  const selectedFactoryFromPicker = useSelector(
    selectSelectedFactoryFromPicker
  );

  // Fetch factories on component mount
  useEffect(() => {
    if (organisationId) {
      dispatch(
        getListOfAllFactories({
          limit: 999,
          offset: 0,
          organisationId,
        })
      );
    }
  }, [dispatch, organisationId]);

  // Debug: Track notes state changes
  useEffect(() => {

  }, [notesState]);

  // Debug: Track submission state changes
  useEffect(() => {

  }, [submissionState]);

  // Handle upload completion
  useEffect(() => {
    Object.entries(uploadStates).forEach(([fieldId, uploadState]) => {
      if (uploadState.uploaded && uploadState.fileData) {
        // Find the section that contains this field
        const sectionsArray = normalizeSections(formConfig?.sections || {});
        sectionsArray.forEach((section) => {
          const sectionId = section.id || section.sectionId;
          const sectionKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`;

          // Check if this section contains the field that was uploaded
          if (
            section.fields &&
            section.fields.some((field) => field.id === fieldId)
          ) {
            // Update the form field with the uploaded file data
            handleFieldChange(sectionKey, fieldId, uploadState.fileData);
          }
        });
      }
    });
  }, [uploadStates, formConfig, formId]);

  const navigate = useNavigate();
  // Navigate to success page when form status becomes "submitted"
  useEffect(() => {

  }, [formStatus, formId]);

  // Get all section save states at once
  // Only consider sections that are actually visible (not hidden by conditional logic)
  const allSectionSaveStates = useSelector((state) => {
    const sections = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const statuses = {};
    sections
      .filter((section) => shouldShowSection(section, reduxFormData)) // Only visible sections
      .forEach((section) => {
        const sectionId = section.id || section.sectionId;
        const sectionSaveStates =
          state.collaborativeForm?.sectionSaveStates || {};
        statuses[sectionId] = sectionSaveStates[sectionId] || {
          saving: false,
          saved: false,
          error: null,
        };
      });
    return statuses;
  });

  // Check if any section is currently saving
  // Only consider visible sections
  const isAnySectionSaving = Object.values(allSectionSaveStates).some(
    (state) => state.saving
  );

  // Get the most recent auto-save time across all sections
  const lastAutoSaveTime = Object.values(allSectionSaveStates).reduce((latest, sectionState) => {
    if (sectionState.lastSaved) {
      const sectionTime = new Date(sectionState.lastSaved).getTime();
      return sectionTime > latest ? sectionTime : latest;
    }
    return latest;
  }, 0);

  // Check if all sections are complete based on status field
  // Only consider sections that are actually visible (not hidden by conditional logic)
  const allSectionsComplete = useSelector((state) => {
    const sections = formConfig?.sections ? normalizeSections(formConfig.sections) : [];

    // Check if all main sections are complete
    const allMainSectionsComplete = sections
      .filter((section) => shouldShowSection(section, reduxFormData)) // Only visible sections
      .every((section) => {
        const status = section.status?.toLowerCase();
        return (
          status === "completed" ||
          status === "incompletewithrequest" ||
          status === "submitted_with_note"
        );
      });

    return allMainSectionsComplete;
  });

  // Clean up form-specific state when formId changes
  useEffect(() => {
    // Reset all form-specific state when switching to a different form
    setInlineNoteSections({});
    setNoteTexts({});
    setShownAlerts(new Set());
    setRealTimeConditionalValues({});
  }, [formId]);

  // Initialize inline note sections for sections that already have submitted notes
  // Only consider sections that are actually visible (not hidden by conditional logic)
  useEffect(() => {
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const newInlineNoteSections = {};
    const newNoteTexts = {};

    sectionsArray
      .filter((section) => shouldShowSection(section, reduxFormData)) // Only visible sections
      .forEach((section) => {
        const sectionId = section.id || section.sectionId;
        const status = section.status?.toLowerCase();

        if (status === "submitted_with_note") {
          newInlineNoteSections[sectionId] = true;
          newNoteTexts[sectionId] = {
            poResponse: section.poResponse || "",
            note: section.requestNote || "",
          };
        }
      });

    // Reset state completely for the current form instead of merging with previous form's state
    setInlineNoteSections(newInlineNoteSections);
    setNoteTexts(newNoteTexts);
  }, [formConfig, formId, reduxFormData]); // Added formId to dependency array to reset when switching forms

  // Get incomplete sections for tooltip message based on status field
  // Only include sections that are actually visible (not hidden by conditional logic)
  const incompleteSections = useSelector((state) => {
    const sections = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const incompleteMainSections = sections
      .filter((section) => {
        // First check if section should be shown based on conditional logic
        if (!shouldShowSection(section, reduxFormData)) {
          return false; // Skip hidden sections
        }
        
        // Then check if section is incomplete
        const status = section.status?.toLowerCase();
        return (
          !status ||
          (status !== "completed" &&
            status !== "incompletewithrequest" &&
            status !== "submitted_with_note")
        );
      })
      .map((section) => section.title);

    return incompleteMainSections;
  });

  // Watch for changes in conditional fields to trigger re-renders
  useEffect(() => {
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const conditionalSections = sectionsArray.filter(section => section.conditional);
    
    if (conditionalSections.length > 0) {
      
      // Initialize real-time conditional values with current database/Redux values
      // BUT only if they don't already exist (preserve user's current input)
      const initialConditionalValues = {};
      
      conditionalSections.forEach(section => {
        const { section: conditionalSection, field } = section.conditional;
        const conditionalSectionObj = sectionsArray.find(s => 
          (s.id || s.sectionId) === conditionalSection
        );
        
        if (conditionalSectionObj) {
          const conditionalSectionKey = `form_${formId}_section_${conditionalSection}_key_${conditionalSectionObj.uniqueKey}`;
          const conditionalSectionData = reduxFormData[conditionalSectionKey] || {};
          const fieldValue = conditionalSectionObj.data?.[field] || conditionalSectionData[field];
          
          // Store the current value to detect changes
          if (fieldValue !== undefined) {
            
            // Initialize real-time values with current values ONLY if they don't exist
            // This prevents overriding user's current input during re-renders
            if (!initialConditionalValues[conditionalSectionKey]) {
              initialConditionalValues[conditionalSectionKey] = {};
            }
            initialConditionalValues[conditionalSectionKey][field] = fieldValue;
          }
        }
      });
      
      // Set initial real-time values, but preserve existing user input
      setRealTimeConditionalValues(prev => {
        const updated = { ...prev };
        Object.entries(initialConditionalValues).forEach(([sectionKey, fields]) => {
          if (!updated[sectionKey]) {
            updated[sectionKey] = {};
          }
                      Object.entries(fields).forEach(([fieldId, value]) => {
              // Only set if the field doesn't already have a real-time value
              // This preserves user's current input
              if (updated[sectionKey][fieldId] === undefined) {
                updated[sectionKey][fieldId] = value;
              } else {
              }
            });
        });
        return updated;
      });
    }
  }, [formConfig, reduxFormData, formId]);

  // Create tooltip message
  const getSubmitTooltipMessage = () => {
    if (allSectionsComplete) {
      return "Ready to submit! All sections are completed or submitted with notes.";
    }

    if (incompleteSections.length === 0) {
      return "Please complete all required fields or submit sections with notes.";
    }

    const sectionList = incompleteSections.join(", ");
    const message =
      incompleteSections.length === 1
        ? `Complete the ${sectionList} section or submit it with a note to enable form submission.`
        : `Complete the following sections or submit them with notes: ${sectionList}`;

    return message;
  };

  // Debug: Log which sections are visible vs hidden for submit logic
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
      const visibleSections = sectionsArray.filter(section => shouldShowSection(section, reduxFormData));
      const hiddenSections = sectionsArray.filter(section => !shouldShowSection(section, reduxFormData));
      
    }
  }, [formConfig, reduxFormData, incompleteSections, allSectionsComplete]);

  // Handle form submission
  const handleFormSubmit = () => {


    if (!formId) {
     
      return;
    }

    if (!userId) {
      return;
    }

    if (!organisationId) {
      return;
    }

    if (!allSectionsComplete) {
 
      return;
    }

    if (isAnySectionSaving) {
      // console.error("[JsonFormRenderer] Cannot submit - sections still saving");
      return;
    }

    // Gather all form data
    // Only include sections that are actually visible (not hidden by conditional logic)
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const completeFormData = {};



    sectionsArray
      .filter((section) => shouldShowSection(section, reduxFormData)) // Only visible sections
      .forEach((section) => {
        const sectionId = section.id || section.sectionId;
        const sectionKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`;
        const sectionData = reduxFormData[sectionKey] || {};

        completeFormData[section.uniqueKey] = {
          sectionId,
          sectionKey: section.uniqueKey,
          sectionData,
          sectionTitle: section.title,

          status: section.status,
          poResponse: section.poResponse,
          requestNote: section.requestNote,
        };

      });



    const submissionPayload = {
      formId,
      userId,
      organisationId,
      formData: completeFormData,
      timestamp: new Date().toISOString(),
    };


    // Dispatch form submission action
    try {
      // console.log("[JsonFormRenderer] About to dispatch action...");
      dispatch(formDataSubmit({ formId, payload: submissionPayload, isFormSubmission: true }));
      // console.log("[JsonFormRenderer] formDataSubmit dispatched successfully");

      // Navigate to success page after dispatching the action
      const targetUrl = `/form-submission-success?id=${formId}`;
      navigate(targetUrl);
    } catch (error) {
      // Handle error silently
    }
  };

  // Defensive approach: Warn user if auto-save is in progress
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isAnySectionSaving) {
        const message =
          "Auto-save is in progress. Please wait for it to complete before leaving.";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isAnySectionSaving]);

  // Create form-specific Redux data for ProgressTracker
  // Only include sections that are actually visible (not hidden by conditional logic)
  const formSpecificReduxData = {};
  if (formConfig && formId) {
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    sectionsArray
      .filter((section) => shouldShowSection(section, reduxFormData)) // Only visible sections
      .forEach((section) => {
        const sectionId = section.id || section.sectionId;
        const formSpecificKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`;
        const legacyKey = `section_${sectionId}`;

        // Map form-specific data to legacy key for ProgressTracker compatibility
        if (reduxFormData[formSpecificKey]) {
          formSpecificReduxData[legacyKey] = reduxFormData[formSpecificKey];
        }
      });
  }

  // Initialize styled components when formConfig changes
  useEffect(() => {
    if (formConfig?.styling) {
      setStyledTextField(() => createStyledTextField(formConfig.styling));
      setStyledFormControl(() => createStyledFormControl(formConfig.styling));
      setPrimaryButton(() => createStyledButton(formConfig.styling, "primary"));
      setSecondaryButton(() =>
        createStyledButton(formConfig.styling, "secondary")
      );
    }
  }, [formConfig]);

  // Initialize form data with default values for sections only
  useEffect(() => {
    if (formConfig && formId) {
      const initialData = {};

      // Section defaults - handle both array and object formats
      const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
      sectionsArray.forEach((section) => {
        // Use original section.id if available, otherwise use sectionId from transformed format
        const sectionId = section.id || section.sectionId;
        const sectionKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`; // Include formId

        initialData[sectionKey] = {};

        if (section.fields) {
          section.fields.forEach((field) => {
            initialData[sectionKey][field.id] =
              field.defaultValue || (field.type === "checkbox" || field.type === "multiselect" ? [] : "");
            
            // Initialize additional inputs for checkbox fields
            if (field.type === "checkbox" && field.additionalInputs && field.additionalInputs.length > 0) {
              // Check if we have existing data in Redux for this section
              const existingSectionData = reduxFormData[sectionKey] || {};
              
              field.additionalInputs.forEach((inputField) => {
                // Use existing value from Redux if available, otherwise use defaultValue
                const defaultValue = inputField.type === 'multiselect' ? [] : "";
                initialData[sectionKey][inputField.id] = existingSectionData[inputField.id] !== undefined 
                  ? existingSectionData[inputField.id]
                  : (inputField.defaultValue || defaultValue);
              });
            }
            
            // Initialize additional inputs for certificate section fields
            if (field.type === "certificate_section" && field.additionalInputs && field.additionalInputs.length > 0) {
              // Check if we have existing data in Redux for this section
              const existingSectionData = reduxFormData[sectionKey] || {};
              
              
              field.additionalInputs.forEach((inputField) => {
                // Use existing value from Redux if available, otherwise use defaultValue
                const defaultValue = inputField.type === 'multiselect' ? [] : "";
                const value = existingSectionData[inputField.id] !== undefined 
                  ? existingSectionData[inputField.id]
                  : (inputField.defaultValue || defaultValue);
                
                initialData[sectionKey][inputField.id] = value;
                
              });
            }
          });
        }
      });

      setLocalFormData(initialData);
    }
  }, [formConfig, formId, reduxFormData]);

  // Auto-save function with debouncing
  const debouncedAutoSave = useCallback(
    debounce(
      (sectionId, formId, userId, organisationId, currentSectionData) => {
        // Get the uniqueKey from sectionKeyMapping
        const sectionKeyMapping = formConfig?.sectionKeyMapping || {};

        const uniqueKey = sectionKeyMapping[sectionId];

        if (!uniqueKey) {
          // console.error(
          //   `[Auto-Save] UniqueKey not found for sectionId ${sectionId}`,
          //   {
          //     sectionId,
          //     availableMappings: sectionKeyMapping,
          //   }
          // );
          return;
        }
        
        // After auto-save completes, sync real-time values with the saved data
        // This ensures real-time state stays consistent with database
        const sectionKey = `form_${formId}_section_${sectionId}_key_${uniqueKey}`;
        setTimeout(() => {
          setRealTimeConditionalValues(prev => {
            // Only sync if the real-time value doesn't exist or is different
            // This prevents overriding user's current input with stale database data
            const updatedValues = { ...prev };
            if (!updatedValues[sectionKey]) {
              updatedValues[sectionKey] = {};
            }
            
            Object.entries(currentSectionData).forEach(([fieldId, value]) => {
              // Only update if the real-time value doesn't exist or is the same as the new value
              // This prevents the "jerking" effect where user input gets overridden
              if (updatedValues[sectionKey][fieldId] === undefined || 
                  updatedValues[sectionKey][fieldId] === value) {
                updatedValues[sectionKey][fieldId] = value;
              } else {
              }
            });
            
            return updatedValues;
          });
        }, 100); // Small delay to ensure auto-save completes

        // Create filledFields object with all fields that have values
        const filledFields = {};

        // Get the section configuration to know all possible fields
        const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];

        // Find the current section by matching the sectionId
        const currentSection = sectionsArray.find(
          (section) => (section.id || section.sectionId) === sectionId
        );

        if (currentSection && currentSection.fields) {
          // Check ALL fields in the section configuration for any non-empty values
          const sectionKey = `form_${formId}_section_${sectionId}_key_${currentSection.uniqueKey}`;
          const sectionData = reduxFormData[sectionKey] || {};

          currentSection.fields.forEach((field) => {
            let value;
            if (currentSectionData.hasOwnProperty(field.id)) {
              // Use the value from currentSectionData (even if it's an empty string)
              value = currentSectionData[field.id];
            } else if (sectionData.hasOwnProperty(field.id)) {
              // Use the value from sectionData if not present in currentSectionData
              value = sectionData[field.id];
            } else {
              // No value in either, skip
              return;
            }

            // Only add to filledFields if the value is not null or undefined
            // (You may want to allow empty string as a valid value, as per your example)
            if (value !== null && value !== undefined && value !== "") {
              filledFields[field.id] = value;
            }
          });

          // Also check additional inputs for checkbox fields
          currentSection.fields.forEach((field) => {
            if (field.type === "checkbox" && field.additionalInputs && field.additionalInputs.length > 0) {
              field.additionalInputs.forEach((inputField) => {
                let value;
                if (currentSectionData.hasOwnProperty(inputField.id)) {
                  value = currentSectionData[inputField.id];
                } else if (sectionData.hasOwnProperty(inputField.id)) {
                  value = sectionData[inputField.id];
                } else {
                  return;
                }

                if (value !== null && value !== undefined && value !== "") {
                  filledFields[inputField.id] = value;
                }
              });
            }
          });
        } else {
          // Fallback: check only fields that exist in currentSectionData
          Object.keys(currentSectionData).forEach((fieldKey) => {
            const fieldValue = currentSectionData[fieldKey];
            if (
              fieldValue !== null &&
              fieldValue !== undefined &&
              fieldValue !== "" &&
              !(Array.isArray(fieldValue) && fieldValue.length === 0)
            ) {
              filledFields[fieldKey] = fieldValue;
            }
          });
        }


        // Only proceed if there's actual data to save
        if (Object.keys(currentSectionData).length === 0) {
          return;
        }

        // Dispatch dataUpdate action to trigger watchDataUpdateSaga
        dispatch(
          dataUpdate({
            formId,
            userId,
            organisationId,
            sectionId,
            sectionKey: uniqueKey, // Use the uniqueKey from mapping
            sectionData: currentSectionData, // Use the original data - no transformation needed
            filledFields, // Add filledFields for completion calculation
            timestamp: new Date().toISOString(),
          })
        );
      },
      2000
    ), // 2 second debounce
    [dispatch, formConfig]
  );


  // Handle form field changes for sections only
  const handleFieldChange = (sectionKey, fieldId, value) => {
    // Check if this is the "isProductListed" field and value is "No"
    // Show browser alert when user selects "No"
    if (fieldId.includes('isProductListed') && value === 'No') {
      window.alert('This is a requirement within 2 weeks of PO placement. Please initiate this process.');
    }

    // Update local state immediately for responsive UI
    const updatedLocalData = {
      ...localFormData,
      [sectionKey]: {
        ...localFormData[sectionKey],
        [fieldId]: value,
      },
    };

    setLocalFormData(updatedLocalData);
    onFormChange?.(updatedLocalData);

    // Update Redux store for persistence
    dispatch(
      updateFormData({
        sectionKey,
        fieldId,
        value,
      })
    );

    // Check if this field change affects conditional logic and trigger re-render
    const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
    const affectedConditionalSections = sectionsArray.filter(section => 
      section.conditional && 
      section.conditional.section === sectionKey.split('_section_')[1]?.split('_key_')[0] &&
      section.conditional.field === fieldId
    );
    
    if (affectedConditionalSections.length > 0) {
      
      // Immediately update real-time conditional values for instant show/hide
      setRealTimeConditionalValues(prev => {
        const updated = {
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            [fieldId]: value
          }
        };
        return updated;
      });
      
      // Trigger re-render for conditional logic
      setConditionalLogicUpdate(prev => prev + 1);
    }

    // Extract section ID for auto-save (remove form prefix and key suffix)
    // sectionKey format: form_${formId}_section_${sectionId}_key_${uniqueKey}
    const sectionIdMatch = sectionKey.match(/form_\d+_section_(.+?)_key_/);
    const sectionId = sectionIdMatch
      ? sectionIdMatch[1]
      : sectionKey.replace(`form_${formId}_section_`, "").split("_key_")[0];



    // Get the updated section data (including the new field value)
    const updatedSectionData = {
      ...reduxFormData[sectionKey],
      [fieldId]: value,
    };

    // Trigger debounced auto-save with the current updated data
    debouncedAutoSave(
      sectionId,
      formId,
      userId,
      organisationId,
      updatedSectionData
    );
  };

  // Handle trader question - toggle trader section
  const handleAddTrader = (firstSection, secondSection) => {
    // Check if trader section already exists in form structure
    const existingTraderSection = Object.values(formConfig?.sections || {}).find(section => 
      section.sectionType === 'Trader' && 
      section.title.includes(firstSection) && 
      section.title.includes(secondSection)
    );
    
    if (existingTraderSection) {
      // Trader section exists, toggle its visibility by updating form structure
      const updatedFormStructure = {
        ...formConfig,
        sections: {
          ...formConfig.sections,
          [existingTraderSection.uniqueKey]: {
            ...existingTraderSection,
            isVisible: !existingTraderSection.isVisible
          }
        }
      };
      
      // Update form structure in Redux
      dispatch(updateFormData({
        sectionKey: 'formStructure',
        fieldId: 'sections',
        value: updatedFormStructure.sections,
      }));
      
      
      // Use dedicated trader API to toggle visibility
      // Include fieldDefinitions from existing section or use default
      const existingFieldDefinitions = existingTraderSection.fields || traderSectionFieldDefinitions;
      
      dispatch(addTraderSectionRequest({
        formId,
        organisationId,
        traderData: {
          firstSection: existingTraderSection.firstSection,
          secondSection: existingTraderSection.secondSection,
          sectionData: {
            ...existingTraderSection.data,
            isVisible: !existingTraderSection.isVisible
          },
          fieldDefinitions: existingFieldDefinitions
        }
      }));
    } else {
      // Create new trader section using fieldDefinitions from config
      const traderSectionId = `trader_${firstSection}_${secondSection}_${Date.now()}`;
      
      const traderSectionData = {
        id: traderSectionId,
        type: 'trader',
        title: `Trader between ${firstSection} and ${secondSection}`,
        firstSection,
        secondSection,
        isVisible: true,
        createdAt: new Date().toISOString(),
      };
      
      // Use dedicated trader API to create new trader section
      // Pass fieldDefinitions from config to the backend
      dispatch(addTraderSectionRequest({
        formId,
        organisationId,
        traderData: {
          firstSection: firstSection,
          secondSection: secondSection,
          sectionData: traderSectionData,
          fieldDefinitions: traderSectionFieldDefinitions
        }
      }));
    }
  };


  // Handle trader question - remove trader section
  const handleRemoveTrader = (firstSection, secondSection) => {
    // Find the existing trader section
    const existingTraderSection = Object.values(formConfig?.sections || {}).find(section => 
      section.sectionType === 'Trader' && 
      section.title.includes(firstSection) && 
      section.title.includes(secondSection)
    );
    
    if (existingTraderSection) {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to remove the trader section between "${firstSection}" and "${secondSection}"? This action cannot be undone.`
      );
      
      if (confirmed) {
        // Dispatch delete action
        dispatch(deleteTraderSectionRequest({
          formId,
          traderId: existingTraderSection.uniqueKey
        }));
      }
    }
  };

  const handleDeleteFile = (fileId) => {
    // Show confirmation dialog
    const confirmed = window.confirm("Are you sure you want to delete this file?");
    if (confirmed) {
      // Deleting file with ID
      // Dispatch delete file action
      dispatch(deleteFileRequest({
        formId: formId,
        fileId: fileId
      }));
    }
  };

  // Check conditional logic - check actual dropdown values
  const shouldShowField = (field, sectionData, completeFormData = null, currentSectionKey = null, localFormData = null) => {
    // Check if field should be shown on supplier platform
    if (field.showOnSupplier === false) return false;
    
    if (!field.conditional) return true;

    const { field: conditionField, condition, value } = field.conditional;
    
    // For ID fields, we need to check the actual values of the "Have ID?" dropdowns
    // These are stored in the current section's data - MUST be section-specific
    let fieldValue = "";
    
    // For certificate fields, the field IDs are already prefixed at the source
    // The conditional field reference should also be prefixed
    if (field.id && field.id.includes('_') && field.id.startsWith('certificate_')) {
      // For certificate fields, the conditionField should already be prefixed
      // e.g., field.id = "certificate_BLUESIGN_productCode", conditionField = "certificate_BLUESIGN_isProductListed"
      if (sectionData && sectionData.hasOwnProperty(conditionField)) {
        fieldValue = sectionData[conditionField];
      }
      
      // If not found in sectionData, check localFormData as fallback
      if (fieldValue === "" && localFormData && localFormData[currentSectionKey]) {
        const localSectionData = localFormData[currentSectionKey];
        if (localSectionData && localSectionData.hasOwnProperty(conditionField)) {
          fieldValue = localSectionData[conditionField];
        }
      }
      
    } else if (sectionData && sectionData.hasOwnProperty(conditionField)) {
      // Regular fields - direct access
      fieldValue = sectionData[conditionField];
    }
    
    
    
    // If not found in current section, check basicInformation data as fallback
    if (fieldValue === "" && completeFormData && completeFormData.basicInformation && completeFormData.basicInformation.data) {
      if (completeFormData.basicInformation.data.hasOwnProperty(conditionField)) {
        fieldValue = completeFormData.basicInformation.data[conditionField];
      }
    }
    
    // If still no value, check saved values from database in form structure
    if (fieldValue === "" && formConfig && formConfig.sections) {
      // Find the current section in the form config to get its specific defaults
      const sectionsArray = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
      const currentSection = sectionsArray.find(section => {
        const sectionId = section.id || section.sectionId;
        const sectionKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`;
        return sectionKey === currentSectionKey;
      });
      
      if (currentSection && currentSection.fields) {
        // Check regular fields first (like the working parent company logic)
        const conditionFieldConfig = currentSection.fields.find(
          (f) => f.id === conditionField
        );
        if (conditionFieldConfig && conditionFieldConfig.defaultValue) {
          fieldValue = conditionFieldConfig.defaultValue;
        }
      }
    }
    

    switch (condition) {
      case "contains":
        return fieldValue?.toLowerCase().includes(value.toLowerCase());
      case "containsAny":
        if (!Array.isArray(fieldValue)) return false;
        return fieldValue.some(item =>
          Array.isArray(value)
            ? value.some(val => item === val)
            : item === value
        );
      case "equals":
        return fieldValue === value;
      case "not_equals":
        return fieldValue !== value;
      default:
        return true;
    }
  };

  // Render different field types for sections only
  // Helper function to determine if a field should show asterisk
  // Since all visible fields are required (either required or conditionalRequired), always show asterisk
  const shouldShowAsterisk = (field, sectionData = {}) => {
    return true; // All visible fields are required
  };

  const renderField = (
    field,
    sectionKey,
    sectionData = {},
    sectionId,
    files = [],
    isSectionApproved = false
  ) => {
    // Use form data first, then fallback to defaultValue from field config only for initial load
    const formValue = sectionData[field.id];
    const value =
      formValue !== undefined ? formValue : field.defaultValue || "";

    const isDisabled = isSectionApproved;
    const showAsterisk = shouldShowAsterisk(field, sectionData);

    // Handle display-only fields
    if (field.displayOnly) {
      return (
        <Box key={field.id}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#333333", fontWeight: 600 }}
            >
              {showAsterisk ? `${field.label}*` : field.label}
            </Typography>
            <InfoOutlinedIcon sx={{ color: "#666666", fontSize: "16px" }} />
          </Box>
          <Typography variant="body1" sx={{ color: "#333333", mt: 1 }}>
            {value}
          </Typography>
        </Box>
      );
    }

    switch (field.type) {
      case "text":
        return (
          <StyledTextField
            key={field.id}
            fullWidth
            label={showAsterisk ? `${field.label}*` : field.label}
            value={value}
            onChange={(e) =>
              handleFieldChange(sectionKey, field.id, e.target.value)
            }
            variant="outlined"
            size="small"
            disabled={isDisabled}
          />
        );

      case "select":
        // Handle dynamic options from database/formConfig
        let optionsToRender = field.options || [];
        if (field.dynamicOptions && field.dynamicOptions.sourceField) {
          // Get source field value from multiple possible locations
          let sourceFieldValue = sectionData[field.dynamicOptions.sourceField];
          
          // If not found in sectionData, try localFormData
          if (!sourceFieldValue && localFormData[sectionKey]) {
            sourceFieldValue = localFormData[sectionKey][field.dynamicOptions.sourceField];
          }
          
          // If still not found, try to get from the field's defaultValue
          if (!sourceFieldValue && field.dynamicOptions.sourceField === 'manufacturingProcesses') {
            const currentSectionFields = sectionData?.fields || [];
            const manufacturingProcessesField = currentSectionFields.find(
              f => f.id === 'manufacturingProcesses'
            );
            if (manufacturingProcessesField && manufacturingProcessesField.defaultValue) {
              sourceFieldValue = manufacturingProcessesField.defaultValue;
            }
          }
          
          if (sourceFieldValue && field.dynamicOptions.mapping) {
            // Use database-driven mapping to get options
            const dynamicOptions = new Set();
            
            // If sourceFieldValue is an array (multiselect), combine options from all selected values
            const selectedValues = Array.isArray(sourceFieldValue) ? sourceFieldValue : [sourceFieldValue];
            
            selectedValues.forEach(selectedValue => {
              if (field.dynamicOptions.mapping[selectedValue]) {
                field.dynamicOptions.mapping[selectedValue].forEach(option => {
                  dynamicOptions.add(option);
                });
              }
            });
            
            // Convert to sorted array
            const dynamicOptionsArray = Array.from(dynamicOptions).sort();
            
            // Merge static options (from field.options) with dynamic options
            // Static options come first (in their original order), then dynamic options
            const staticOptions = field.options || [];
            const mergedOptions = [...staticOptions];
            
            // Add dynamic options that aren't already in static options
            dynamicOptionsArray.forEach(option => {
              if (!mergedOptions.includes(option)) {
                mergedOptions.push(option);
              }
            });
            
            optionsToRender = mergedOptions;
          }
        }

        return (
          <StyledFormControl key={field.id} fullWidth size="small">
            <InputLabel>
              {showAsterisk ? `${field.label}*` : field.label}
            </InputLabel>
            <Select
              value={value}
              label={showAsterisk ? `${field.label}*` : field.label}
              onChange={(e) =>
                handleFieldChange(sectionKey, field.id, e.target.value)
              }
              disabled={isDisabled}
            >
              {optionsToRender.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        );

      case "multiselect":
        // Handle dynamic options from database/formConfig
        let multiselectOptionsToRender = field.options || [];
        if (field.dynamicOptions && field.dynamicOptions.sourceField) {
          // Get source field value from multiple possible locations
          let sourceFieldValue = sectionData[field.dynamicOptions.sourceField];
          
          // If not found in sectionData, try localFormData
          if (!sourceFieldValue && localFormData[sectionKey]) {
            sourceFieldValue = localFormData[sectionKey][field.dynamicOptions.sourceField];
          }
          
          // If still not found, try to get from the field's defaultValue
          if (!sourceFieldValue && field.dynamicOptions.sourceField === 'manufacturingProcesses') {
            const currentSectionFields = sectionData?.fields || [];
            const manufacturingProcessesField = currentSectionFields.find(
              f => f.id === 'manufacturingProcesses'
            );
            if (manufacturingProcessesField && manufacturingProcessesField.defaultValue) {
              sourceFieldValue = manufacturingProcessesField.defaultValue;
            }
          }
          
          if (sourceFieldValue && field.dynamicOptions.mapping) {
            // Use database-driven mapping to get options
            const dynamicOptions = new Set();
            
            // If sourceFieldValue is an array (multiselect), combine options from all selected values
            const selectedValues = Array.isArray(sourceFieldValue) ? sourceFieldValue : [sourceFieldValue];
            
            selectedValues.forEach(selectedValue => {
              if (field.dynamicOptions.mapping[selectedValue]) {
                field.dynamicOptions.mapping[selectedValue].forEach(option => {
                  dynamicOptions.add(option);
                });
              }
            });
            
            // Convert to sorted array
            const dynamicOptionsArray = Array.from(dynamicOptions).sort();
            
            // Merge static options (from field.options) with dynamic options
            // Static options come first (in their original order), then dynamic options
            const staticOptions = field.options || [];
            const mergedOptions = [...staticOptions];
            
            // Add dynamic options that aren't already in static options
            dynamicOptionsArray.forEach(option => {
              if (!mergedOptions.includes(option)) {
                mergedOptions.push(option);
              }
            });
            
            multiselectOptionsToRender = mergedOptions;
          }
        }

        // Filter out invalid selections that are no longer available in the options
        const currentValue = Array.isArray(value) ? value : [];
        const validSelections = currentValue.filter(selectedValue => 
          multiselectOptionsToRender.includes(selectedValue)
        );
        
        // If there are invalid selections, update the field value to remove them
        if (validSelections.length !== currentValue.length) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => {
            handleFieldChange(sectionKey, field.id, validSelections);
          }, 0);
        }

        return (
          <StyledFormControl key={field.id} fullWidth size="small">
            <InputLabel>
              {showAsterisk ? `${field.label}*` : field.label}
            </InputLabel>
            <Select
              multiple
              value={validSelections}
              label={showAsterisk ? `${field.label}*` : field.label}
              onChange={(e) => {
                const selectedValues = e.target.value;
                handleFieldChange(sectionKey, field.id, selectedValues);
              }}
              renderValue={(selected) => selected.join(', ')}
              disabled={isDisabled}
            >
              {multiselectOptionsToRender.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={validSelections.includes(option)} />
                  {option}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        );

      case "radio":
        return (
          <Box key={field.id}>
            <FormLabel
              component="legend"
              sx={{
                color: "#666666",
                fontSize: "14px",
                fontWeight: 500,
                mb: 1,
              }}
            >
              {showAsterisk ? `${field.label}*` : field.label}
            </FormLabel>
            <RadioGroup
              row
              value={value}
              onChange={(e) =>
                handleFieldChange(sectionKey, field.id, e.target.value)
              }
              disabled={isDisabled}
            >
              {field.options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={option}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case "checkbox":
        return (
          <Box key={field.id}>
            {/* Main checkbox section */}
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={field.additionalConfig?.showUploadEvidence ? 3 : 12}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 2,
                  }}
                >
                  {showAsterisk ? `${field.label}*` : field.label}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={field.additionalConfig?.showUploadEvidence ? 9 : 12}
              >
                {/* Additional Inputs Section */}
                {field.additionalInputs && field.additionalInputs.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                      {field.additionalInputs.map((inputField) => {
                        const inputValue = localFormData[sectionKey]?.[inputField.id] || inputField.defaultValue || '';
                        const showInputAsterisk = shouldShowAsterisk(inputField, localFormData[sectionKey] || {});
                        
                        // Handle different input types
                        if (inputField.type === 'multiselect') {
                          return (
                            <Grid
                              item
                              xs={inputField.gridSize?.xs || 12}
                              sm={inputField.gridSize?.sm || 6}
                              md={inputField.gridSize?.md || 3}
                              key={inputField.id}
                            >
                              <StyledFormControl
                                fullWidth
                                size="small"
                                disabled={isDisabled}
                              >
                                <InputLabel>
                                  {showInputAsterisk ? `${inputField.label}*` : inputField.label}
                                </InputLabel>
                                <Select
                                  multiple
                                  value={Array.isArray(inputValue) ? inputValue : []}
                                  onChange={(e) => {
                                    const selectedValues = e.target.value;
                                    handleFieldChange(sectionKey, inputField.id, selectedValues);
                                  }}
                                  label={showInputAsterisk ? `${inputField.label}*` : inputField.label}
                                  disabled={isDisabled}
                                  renderValue={(selected) => selected.join(', ')}
                                >
                                  {inputField.options?.map((option) => (
                                    <MenuItem key={option} value={option}>
                                      <Checkbox checked={Array.isArray(inputValue) && inputValue.includes(option)} />
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </StyledFormControl>
                            </Grid>
                          );
                        } else {
                          // Default to text input
                          return (
                            <Grid
                              item
                              xs={inputField.gridSize?.xs || 12}
                              sm={inputField.gridSize?.sm || 6}
                              md={inputField.gridSize?.md || 3}
                              key={inputField.id}
                            >
                              <StyledTextField
                                fullWidth
                                label={showInputAsterisk ? `${inputField.label}*` : inputField.label}
                                value={inputValue}
                                onChange={(e) =>
                                  handleFieldChange(sectionKey, inputField.id, e.target.value)
                                }
                                variant="outlined"
                                size="small"
                                disabled={isDisabled}
                              />
                            </Grid>
                          );
                        }
                      })}
                    </Grid>
                  </Box>
                )}

                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "14px",
                    mb: 2,
                  }}
                >
                  Check all the options that apply*
                </Typography>

                <FormGroup>
                  {field.options.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={(value || []).includes(option)}
                          onChange={(e) => {
                            const currentValues = value || [];
                            const newValues = e.target.checked
                              ? [...currentValues, option]
                              : currentValues.filter((v) => v !== option);
                            handleFieldChange(sectionKey, field.id, newValues);
                          }}
                          disabled={isDisabled}
                          sx={{
                            color: "#666666",
                            "&.Mui-checked": { color: "#4f46e5" },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "14px", color: "#333333" }}
                        >
                          {option}
                        </Typography>
                      }
                      sx={{ mb: 1 }}
                    />
                  ))}
                </FormGroup>

                {/* Upload Evidence Section */}
                {field.additionalConfig?.showUploadEvidence &&
                  (value || []).length > 0 &&
                  (value || []).some(option => !option.includes("None of these apply")) && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#333333", fontWeight: 600, mb: 2 }}
                      >
                        {field.additionalConfig.uploadEvidenceText ||
                          "Upload Evidence for Selected Options*"}
                      </Typography>
                      
                      {/* Individual Upload Buttons for Each Selected Option */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(value || [])
                          .filter(selectedOption => !selectedOption.includes("None of these apply"))
                          .map((selectedOption, index) => (
                          <Box 
                            key={`${selectedOption}-${index}`}
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 1,
                              p: 2,
                              backgroundColor: "#ffffff",
                              borderRadius: "6px",
                              border: "1px solid #e5e7eb"
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ 
                                  color: "#333333", 
                                  fontWeight: 500,
                                }}
                              >
                                {index + 1}. {selectedOption}
                              </Typography>
                              {field.additionalConfig?.tooltipInfo && (
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {field.additionalConfig.tooltipInfo.information}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#000000",
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                      fontWeight: 400,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {field.additionalConfig.tooltipInfo.title}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                              {/* Individual Upload Button for this option */}
                              <Box sx={{ minWidth: 'fit-content' }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<UploadIcon />}
                                  onClick={() =>
                                    handleOpen(
                                      sectionKey,
                                      `${field.id}_upload_evidence_${index}`,
                                      sectionId,
                                      "Manufacturing_Process",
                                      selectedOption
                                    )
                                  }
                                  disabled={isDisabled}
                                  sx={{
                                    color: "#4f46e5",
                                    borderColor: "#4f46e5",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    padding: "4px 10px",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    "&:hover": {
                                      backgroundColor: "#f8f7ff",
                                    borderColor: "#4338ca",
                                  },
                                }}
                              >
                                Upload Files
                              </Button>
                            </Box>

                              {/* Uploaded Documents Section for this option */}
                              <Box sx={{ 
                                flex: 1,
                                backgroundColor: "#f9fafb",
                                padding: 2,
                                borderRadius: "8px"
                              }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: "#333333", fontWeight: 600, mb: 2, fontSize: "18px" }}
                              >
                                Uploaded Documents
                              </Typography>
                              <Grid container spacing={2}>
                                {files
                                  .filter(
                                    (file) => file.type === "Manufacturing_Process" && 
                                    (file.option === selectedOption || !file.option) // Show files for this specific option or legacy files without option
                                  )
                                  .map((file, fileIndex) => {
                                    // Ensure file has a unique ID - generate one if it doesn't exist
                                    const fileId = file.id || `legacy_${file.key}_${fileIndex}`;
                                    return (
                                      <Grid item xs={12} key={fileIndex}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: "20px" }}>
                                            <Link
                                              sx={{
                                                fontWeight: 500,
                                                fontSize: "16px",
                                                color: "#666666",
                                                textDecoration: "underline",
                                                textDecorationColor: "#45464E",
                                                fontFamily: "Inter",
                                                "&:hover": {
                                                  textDecorationColor:
                                                    "rgba(69, 70, 78, 0.8)",
                                                },
                                              }}
                                              href={file.url}
                                              target="_blank"
                                            >
                                              {file?.name}
                                            </Link>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              onClick={() => handleDeleteFile(fileId)}
                                              disabled={isSectionApproved}
                                              sx={{
                                                borderColor: "rgba(53, 56, 205, 1)",
                                                color: "rgba(53, 56, 205, 1)",
                                                backgroundColor: "white",
                                                fontSize: "12px",
                                                padding: "2px 8px",
                                                minWidth: "auto",
                                                height: "24px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                borderRadius: "4px",
                                                "&:hover": {
                                                  borderColor: "rgba(53, 56, 205, 0.8)",
                                                  backgroundColor: "rgba(53, 56, 205, 0.05)",
                                                  color: "rgba(53, 56, 205, 0.8)",
                                                },
                                                "&:disabled": {
                                                  borderColor: "#d1d5db",
                                                  color: "#9ca3af",
                                                  backgroundColor: "#f9fafb",
                                                }
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </Box>
                                          {file?.date && (
                                            <Typography
                                              sx={{
                                                fontSize: 12,
                                                color: "#8B8D97",
                                                fontStyle: "italic",
                                                marginLeft: "auto",
                                              }}
                                            >
                                              Date: {file.date}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                              </Grid>
                              
                              {/* Display upload status for this specific option */}
                              {(() => {
                                const uploadState =
                                  uploadStates[`${field.id}_upload_evidence_${index}`];
                                if (uploadState?.uploading) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mt: 1,
                                        color: "#f59e0b",
                                        fontSize: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: "#f59e0b",
                                          animation:
                                            "pulse 1.5s ease-in-out infinite",
                                          "@keyframes pulse": {
                                            "0%": { opacity: 1 },
                                            "50%": { opacity: 0.5 },
                                            "100%": { opacity: 1 },
                                          },
                                        }}
                                      />
                                      Uploading file...
                                    </Typography>
                                  );
                                }

                                // Removed upload success message

                                if (uploadState?.error) {
                                  return (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mt: 1,
                                        color: "#ef4444",
                                        fontSize: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <WarningIcon sx={{ fontSize: "14px" }} />
                                      Upload failed: {uploadState.error}
                                    </Typography>
                                  );
                                }

                                return null;
                              })()}
                            </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
              </Grid>
            </Grid>
          </Box>
        );

      case "certificate_section":
        // Certificate section fields are handled by the main grid container
        // Return null here as they will be rendered by the main field mapping
        return null;

      case "file":
        return (
          <Box key={field.id}>
            <Typography variant="body2" sx={{ mb: 1, color: "#666666" }}>
              {showAsterisk ? `${field.label}*` : field.label}
            </Typography>
            
            {/* Display uploaded certificate files inline for certificate upload fields */}
            {field.label.includes("Upload Certificate") && (() => {
              // Extract certificate type from field ID (e.g., "certificate_GOTS_certificateUpload" -> "GOTS")
              const certTypeMatch = field.id.match(/certificate_([^_]+)_/);
              if (!certTypeMatch) return null;
              
              const certificateType = certTypeMatch[1];
              const certificateFiles = files.filter(file => 
                file.certificateType === certificateType && 
                file.fieldId === field.id &&
                file.type !== "Manufacturing_Process"
              );
              
              // If there are uploaded files, show side-by-side layout
              if (certificateFiles.length > 0) {
                return (
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                    {/* Upload Button Section */}
                    <Box sx={{ minWidth: 'fit-content' }}>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => handleOpen(sectionKey, field.id, sectionId)}
                        disabled={isDisabled}
                        sx={{
                          color: "#4f46e5",
                          borderColor: "#4f46e5",
                          textTransform: "none",
                          fontWeight: 500,
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "14px",
                          "&:hover": {
                            backgroundColor: "#f8f7ff",
                            borderColor: "#4338ca",
                          },
                        }}
                      >
                        {field.label.includes("Upload")
                          ? field.label.replace("*", "")
                          : "Choose File"}
                      </Button>
                    </Box>

                    {/* Uploaded Documents Section */}
                    <Box sx={{ 
                      flex: 1,
                      backgroundColor: "#33331110",
                      padding: 2,
                      borderRadius: "8px"
                    }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#333333",
                          fontWeight: 600,
                          fontSize: "18px",
                          mb: 1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                      <Grid container spacing={1}>
                        {certificateFiles.map((file, index) => {
                          // Ensure file has a unique ID - generate one if it doesn't exist
                          const fileId = file.id || `legacy_${file.key}_${index}`;
                          return (
                            <Grid item xs={12} key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: "20px" }}>
                                  <Link
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "16px",
                                      color: "#45464E",
                                      textDecoration: "underline",
                                      textDecorationColor: "#45464E",
                                      fontFamily: "Inter",
                                      "&:hover": {
                                        textDecorationColor: "rgba(69, 70, 78, 0.8)",
                                      },
                                    }}
                                    href={file.url}
                                    target="_blank"
                                  >
                                    {file?.name}
                                  </Link>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleDeleteFile(fileId)}
                                    disabled={isSectionApproved}
                                    sx={{
                                      borderColor: "rgba(53, 56, 205, 1)",
                                      color: "rgba(53, 56, 205, 1)",
                                      backgroundColor: "white",
                                      fontSize: "12px",
                                      padding: "2px 8px",
                                      minWidth: "auto",
                                      height: "24px",
                                      textTransform: "none",
                                      fontWeight: 500,
                                      borderRadius: "4px",
                                      "&:hover": {
                                        borderColor: "rgba(53, 56, 205, 0.8)",
                                        backgroundColor: "rgba(53, 56, 205, 0.05)",
                                        color: "rgba(53, 56, 205, 0.8)",
                                      },
                                      "&:disabled": {
                                        borderColor: "#d1d5db",
                                        color: "#9ca3af",
                                        backgroundColor: "#f9fafb",
                                      }
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                                {file?.date && (
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: "#8B8D97",
                                      fontStyle: "italic",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    Date: {file.date}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                );
              }
              
              // If no files uploaded, show just the upload button
              return (
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => handleOpen(sectionKey, field.id, sectionId)}
                  disabled={isDisabled}
                  sx={{
                    color: "#4f46e5",
                    borderColor: "#4f46e5",
                    textTransform: "none",
                    fontWeight: 500,
                    padding: "4px 10px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    "&:hover": {
                      backgroundColor: "#f8f7ff",
                      borderColor: "#4338ca",
                    },
                  }}
                >
                  {field.label.includes("Upload")
                    ? field.label.replace("*", "")
                    : "Choose File"}
                </Button>
              );
            })()}
            
            {/* For non-certificate file fields, show regular upload button */}
            {!field.label.includes("Upload Certificate") && (
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => handleOpen(sectionKey, field.id, sectionId)}
                disabled={isDisabled}
                sx={{
                  color: "#4f46e5",
                  borderColor: "#4f46e5",
                  textTransform: "none",
                  fontWeight: 500,
                  padding: "4px 10px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  "&:hover": {
                    backgroundColor: "#f8f7ff",
                    borderColor: "#4338ca",
                  },
                }}
              >
                {field.label.includes("Upload")
                  ? field.label.replace("*", "")
                  : "Choose File"}
              </Button>
            )}
            
            {/* Display selected file name if available */}
            {(() => {
              const uploadState = uploadStates[field.id];
              if (uploadState?.uploading) {
                return (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: "#f59e0b",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                        animation: "pulse 1.5s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    />
                    Uploading file...
                  </Typography>
                );
              }

              // Removed upload success message

              if (uploadState?.error) {
                return (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: "#ef4444",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <WarningIcon sx={{ fontSize: "14px" }} />
                    Upload failed: {uploadState.error}
                  </Typography>
                );
              }


              return null;
            })()}
          </Box>
        );

      case "date":
        return (
          <StyledTextField
            key={field.id}
            fullWidth
            label={showAsterisk ? `${field.label}*` : field.label}
            type="date"
            value={value}
            onChange={(e) =>
              handleFieldChange(sectionKey, field.id, e.target.value)
            }
            variant="outlined"
            size="small"
            disabled={isDisabled}
            InputLabelProps={{ shrink: true }}
          />
        );

      case "alert":
        // Alert functionality removed - handle through UI components instead
        if (field.message && !shownAlerts.has(field.id)) {
          setShownAlerts(prev => new Set([...prev, field.id]));
        }
        
        // Return null since we don't want to render anything visible
        return null;

        case "subtitle":
          return (
            <Box
              key={field.id}
              sx={{
                mt: 4,
                mb: 0,
                position: "relative",
                paddingLeft: "16px",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  backgroundColor: "#2D3282",
                  borderRadius: "0 2px 2px 0",
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#333333",
                  fontWeight: 600,
                  fontSize: "16px",
                }}
              >
                {field.label}
              </Typography>
            </Box>
          );

      default:
        return null;
    }
  };

  // Debug: Check component props - can be removed in production

  // Main component render
  if (!formConfig || !StyledTextField || !formConfig.sections) {
    return <Typography>Loading form...</Typography>;
  }

  return (
    <Box sx={{ p: 4, mt: 4 }}>
      {/* Preview Content */}
      {formConfig.previewContent && (
        <Box sx={{ mb: 4 }}>
          
          <Typography
            variant="h6"
            sx={{
              color: "#333333",
              fontWeight: 600,
              fontSize: "18px",
              mb: 2,
            }}
          >
            {formConfig.previewContent.messageFrom}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Typography
            variant="body1"
            sx={{
              color: "#333333",
              fontSize: "14px",
              lineHeight: 1.6,
              mb: 10,
            }}
          >
            {formConfig.previewContent.content}
          </Typography>
        </Box>
      )}

      {/* Basic Information - Read Only */}
      {formConfig.basicInformation && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#333333",
              fontWeight: 600,
              fontSize: "18px",
              mb: 3,
            }}
          >
            {formConfig.basicInformation.title}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {formConfig.basicInformation.fields
              .filter(field => field.showOnSupplier !== false) // Filter out fields hidden from supplier
              .map((field) => {
              // Use custom grid size if specified, otherwise use default
              const gridSize = field.gridSize || { xs: 12, sm: 6, md: 3 };
              const value = field.defaultValue || "";
              const showAsterisk = shouldShowAsterisk(field, {});

              return (
                <Grid
                  item
                  xs={gridSize.xs}
                  sm={gridSize.sm}
                  md={gridSize.md}
                  key={field.id}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#333333",
                        fontWeight: 600,
                        fontSize: "14px",
                        mb: 1,
                      }}
                    >
                      {showAsterisk ? `${field.label}*` : field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#666666",
                        fontSize: "14px",
                        minHeight: "20px",
                      }}
                    >
                      {value || "-"}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
            
            {/* Auto-generated Composition Field */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "14px",
                    mb: 1,
                  }}
                >
                  Composition
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#666666",
                    fontSize: "14px",
                    minHeight: "20px",
                    fontStyle: "italic",
                  }}
                >
                  {generateCompositionFromBasicInfo(formConfig.basicInformation.fields)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

             {/* Progress Tracker */}
       <ProgressTracker
         sections={formConfig?.sections ? normalizeSections(formConfig.sections).filter(section => shouldShowSection(section, reduxFormData)) : []}
         overallCompletion={formConfig?.overallCompletion}
         formData={formSpecificReduxData}
         ref={progressTrackerRef}
       />

      {/* Dynamic Sections - Editable Forms */}
      {/* Conditional logic: Sections are filtered based on shouldShowSection function */}
      {/* Real-time toggle system: Uses local state for immediate updates, falls back to database values */}
      {(() => {
        const allSections = formConfig?.sections ? normalizeSections(formConfig.sections) : [];
        const visibleSections = allSections.filter(section => shouldShowSection(section, reduxFormData));
        const hiddenSections = allSections.filter(section => !shouldShowSection(section, reduxFormData));
        
        
        return visibleSections.map((section) => {
        // Use original section.id if available, otherwise use sectionId from transformed format
        const sectionId = section.id || section.sectionId;
        const sectionKey = `form_${formId}_section_${sectionId}_key_${section.uniqueKey}`; // Include formId
        const sectionData = reduxFormData[sectionKey] || {};

        // Get auto-save status for this section
        const sectionAutoSaveStatus = allSectionSaveStates[sectionId] || {
          saving: false,
          saved: false,
          error: null,
        };

        // Check if section has brandActions with status "approve" to disable inputs
        const isSectionApproved = section?.brandActions?.status === "approve";

        return (
          <Box
            key={`${section.uniqueKey || section.id || sectionId}_${conditionalLogicUpdate}`}
            id={`form-section-${sectionId}`}
            data-section-title={section.title}
            data-section-status={(section.status || "").toLowerCase()}
            sx={{ mb: 4, scrollMarginTop: "100px" }}
          >
            {/* Section Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                mt: 5,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 5 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "30px",
                  }}
                >
                  {section.title}
                </Typography>
                <InfoOutlinedIcon
                  variant="outlined"
                  sx={{ color: "#666666", fontSize: "30px", cursor: "pointer" }}
                  onClick={() => {
                    handleOpenCloseFAQDrawer(true);
                    setCurrentSectionTitle(section.title);
                  }}
                />

                {/* Auto-save Status Indicator */}
                {sectionAutoSaveStatus.saving && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                        animation: "pulse 1.5s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "#f59e0b", fontSize: "12px" }}
                    >
                      Auto-saving...
                    </Typography>
                  </Box>
                )}

                {sectionAutoSaveStatus.saved &&
                  !sectionAutoSaveStatus.saving && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        ml: 2,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ color: "#10b981", fontSize: "14px" }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#10b981", fontSize: "12px" }}
                      >
                        Auto-saved
                        {sectionAutoSaveStatus.lastSaved &&
                          ` at ${new Date(
                            sectionAutoSaveStatus.lastSaved
                          ).toLocaleTimeString()}`}
                      </Typography>
                    </Box>
                  )}

                {sectionAutoSaveStatus.error && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "#ef4444", fontSize: "12px" }}
                    >
                      Auto-save failed: {sectionAutoSaveStatus.error}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Section Status:
                </Typography>
                {section.status === "completed" ? (
                  <CheckCircleIcon
                    sx={{ color: "#4ade80", fontSize: "20px" }}
                  />
                ) : section.status === "incompletewithrequest" ||
                  section.status === "submitted_with_note" ? (
                  <WarningIcon sx={{ color: "#f59e0b", fontSize: "20px" }} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ color: "#d1d5db", fontSize: "20px" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "#ef4444", fontSize: "12px" }}
                    >
                      {section.status || "No Status"}
                    </Typography>
                  </Box>
                )}
                {section.status === "completed" ? (
                  <Typography variant="body2" sx={{ color: "#666666", ml: 2 }}>
                    Section completed
                  </Typography>
                ) : section.status === "incompletewithrequest" ||
                  section.status === "submitted_with_note" ? (
                  <Typography variant="body2" sx={{ color: "#666666", ml: 2 }}>
                    Submitted with note
                  </Typography>
                ) : (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        {(() => {
                          // Get fresh data from Redux and merge with local changes
                          const freshSectionData = reduxFormData[sectionKey] || {};
                          const mergedSectionData = {
                            ...freshSectionData,
                            ...(localFormData[sectionKey] || {}),
                          };
                          
                          const incompleteFields = checkIncompleteFieldsForSection(
                            section,
                            mergedSectionData,
                            sectionKey
                          );
                          
                          return incompleteFields.length > 0 ? (
                            <>
                              <Typography
                                sx={{ mb: 1.5, color: "#E5E7EB", fontWeight: 600, fontSize: "0.875rem" }}
                              >
                                The following fields are required to be completed:
                              </Typography>
                              {incompleteFields.map((field, index) => (
                                <Typography
                                  key={index}
                                  sx={{ color: "white", mb: 0.5, fontSize: "0.813rem" }}
                                >
                                  • {field}
                                </Typography>
                              ))}
                            </>
                          ) : (
                            <Typography sx={{ color: "#86EFAC", fontWeight: 500, fontSize: "0.875rem" }}>
                              All required fields are completed!
                            </Typography>
                          );
                        })()}
                      </Box>
                    }
                    arrow
                    placement="bottom-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#1F2937",
                          maxWidth: 400,
                          p: 2,
                          boxShadow: "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
                          "& .MuiTooltip-arrow": {
                            color: "#1F2937",
                          },
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666666",
                        ml: 2,
                        cursor: "pointer",
                        textDecoration: "underline",
                        display: "inline",
                        "&:hover": {
                          color: "#B54708",
                        },
                      }}
                    >
                      Check Incomplete Fields
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Divider */}
            <Divider 
              sx={{ 
                mb: 3, 
                mt: 2,
                borderColor: "#e0e0e0",
                borderWidth: "1px",
                opacity: 0.6
              }} 
            />

            {/* Action Buttons */}
            {section.actions && (
              <Grid
                container
                spacing={3}
                sx={{ justifyContent: "space-between", my: 4 }}
              >
                {section.actions.map((action) => {

                  // TODO: Remove the primary secodnary logic now 
                  // const ButtonComponent =
                  //   action.style === "primary"
                  //     ? PrimaryButton
                  //     : SecondaryButton;


                    const ButtonComponent = PrimaryButton

                
                  const handleActionClick = () => {
                    if (action.id === "requestSubmit") {
                      // Open inline note instead of modal
                      handleOpenInlineNote(sectionId);
                    } else if (action.id === "pickFromPrevious") {
                      // Open facility picker
                      handleOpenFacilityPicker(sectionId);
                    } else if (action.onClick) {
                      action.onClick();
                    } else {
                      // Log unknown actions in development
                      if (process.env.NODE_ENV === 'development') {
                      }
                    }
                  };

                  return (
                    <Grid item key={action.id}>
                      <ButtonComponent onClick={handleActionClick} disabled={isSectionApproved}>
                        {action.label}
                      </ButtonComponent>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <Grid item xs={12}>
              {facilityPickerOpen && facilityPickerSectionId === sectionId && (
                <PrefillFactoryInformationPicker
                  facilityPickerOpen={facilityPickerOpen}
                  handleCloseFacilityPicker={handleCloseFacilityPicker}
                  handleFacilitySelect={handleFacilitySelect}
                />
              )}
            </Grid>
            {/* Section Fields */}
            <Grid container spacing={3}>

              {/* Subtitle mappings configuration to separate sections*/}
              {(() => {
                const SUBTITLE_MAPPINGS = {
                  'name': 'Basic Information',
                  'informationOnParentCompany': 'Parent Company', 
                  'haveWorldlyId': 'Sustainability Program ID',
                  'manufacturingProcesses': 'Manufacturing Processes',
                  'facilityListedAsSubcontractor': 'Additional Questions on Certificate',
                };

                // Function to inject subtitles into fields array
                const injectSubtitles = (fields) => {
                  const result = [];
                  
                  fields.forEach((field, index) => {
                    // Check for ID-based subtitle mapping
                    if (SUBTITLE_MAPPINGS[field.id]) {
                      result.push({
                        id: `subtitle_${field.id}`,
                        type: 'subtitle',
                        label: SUBTITLE_MAPPINGS[field.id],
                        gridSize: { xs: 12, sm: 12, md: 12 }
                      });
                    }
                    
                    // Note: certificate_section subtitles are handled in the expansion logic above
                    
                    result.push(field);
                  });
                  
                  return result;
                };

                const fieldsAfterExpansion = section.fields
                  ?.filter(field => shouldShowField(field, sectionData, reduxFormData, sectionKey, localFormData))
                  ?.flatMap(field => {
                    // Handle certificate_section fields by expanding their additionalInputs
                    if (field.type === 'certificate_section' && field.additionalInputs) {
                      const expandedFields = [];
                      
                      // Add Certificate Requirement subtitle before the certificate fields
                      expandedFields.push({
                        id: `subtitle_certificate_${field.id}`,
                        type: 'subtitle',
                        label: 'Certificate Requirement',
                        gridSize: { xs: 12, sm: 12, md: 12 }
                      });
                      
                      // Add the actual certificate fields
                      const certificateFields = field.additionalInputs
                        .filter(inputField => shouldShowField(inputField, sectionData, reduxFormData, sectionKey, localFormData));
                      expandedFields.push(...certificateFields);
                      
                      // Note: Alert message is now shown via browser alert in handleFieldChange
                      // when user selects "No" for isProductListed
                      
                      return expandedFields;
                    }
                    return [field];
                  });
                
                return injectSubtitles(fieldsAfterExpansion);
              })()
                ?.map((field, index) => {
                  // Use custom grid size if specified, otherwise use defaults
                  const gridSize = field.gridSize || {
                    xs: 12,
                    sm: field.type === "checkbox" ? 12 : 6,
                    md: field.type === "checkbox" ? 12 : 3,
                  };

                  return (
                    <Grid
                      item
                      xs={gridSize.xs}
                      sm={gridSize.sm}
                      md={gridSize.md}
                      key={field.id}
                    >
                      {renderField(
                        field,
                        sectionKey,
                        sectionData,
                        sectionId,
                        section.files,
                        isSectionApproved
                      )}
                    </Grid>
                  );
                })}
            </Grid>

            {/* Inline Note Section */}
            {inlineNoteSections[sectionId] && (
              <Box
                id={`note-section-${sectionId}`}
                sx={{
                  mt: 4,
                  p: 3,

                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 2,
                  }}
                >
                  Explanation on submitting with all required field completed*
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Enter your note here..."
                  value={noteTexts[sectionId]?.note || ""}
                  onChange={(e) =>
                    setNoteTexts((prev) => ({
                      ...prev,
                      [sectionId]: {
                        ...prev[sectionId],
                        note: e.target.value,
                      },
                    }))
                  }
                  disabled={formStatus === "approved" || isSectionApproved}
                  sx={{ mb: 2 }}
                />

                {/* Provide after PO Dropdown */}

                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: "#333333",
                      fontWeight: 600,
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Provide after PO
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={noteTexts[sectionId]?.poResponse || ""}
                      onChange={(e) =>
                        setNoteTexts((prev) => ({
                          ...prev,
                          [sectionId]: {
                            ...prev[sectionId],
                            poResponse: e.target.value,
                          },
                        }))
                      }
                      disabled={formStatus === "approved" || isSectionApproved}
                      displayEmpty
                      sx={{
                        "& .MuiSelect-select": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select an option</em>
                      </MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Show buttons only if form and section are not approved */}
                {formStatus !== "approved" && !isSectionApproved && (() => {
                  // Check if note is already submitted
                  const isNoteSubmitted = section.status?.toLowerCase() === "submitted_with_note";
                  
                  return (
                    <Box
                      sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                    >
                      {/* Left button: Close/Withdraw */}
                      {isNoteSubmitted ? (
                        // If note is submitted, show "Withdraw Request" button
                        <Button
                          onClick={() => handleWithdrawNote(sectionId)}
                          variant="outlined"
                          sx={{
                            color: "#666666",
                            borderColor: "#666666",
                            textTransform: "none",
                            fontWeight: 500,
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontSize: "14px",
                            "&:hover": {
                              backgroundColor: "#f8f9fa",
                              borderColor: "#333333",
                            },
                          }}
                        >
                          Withdraw Request to "Submit with Note"
                        </Button>
                      ) : (
                        // If note is not submitted, show "Close" button
                        <Button
                          onClick={() => handleCancelNote(sectionId)}
                          variant="outlined"
                          sx={{
                            color: "#666666",
                            borderColor: "#666666",
                            textTransform: "none",
                            fontWeight: 500,
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontSize: "14px",
                            "&:hover": {
                              backgroundColor: "#f8f9fa",
                              borderColor: "#333333",
                            },
                          }}
                        >
                          Close "Submit with Note" Section
                        </Button>
                      )}
                      
                      {/* Right button: Submit/Update */}
                      <Button
                        onClick={() => handleNoteSubmit(sectionId)}
                        variant="contained"
                        disabled={!noteTexts[sectionId]?.note?.trim()}
                        sx={{
                          backgroundColor: "#f59e0b",
                          color: "#ffffff",
                          textTransform: "none",
                          fontWeight: 500,
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "14px",
                          "&:hover": {
                            backgroundColor: "#d97706",
                          },
                          "&:disabled": {
                            backgroundColor: "#d1d5db",
                            color: "#9ca3af",
                          },
                        }}
                      >
                        {isNoteSubmitted ? "Update Note" : "Submit with Note"}
                      </Button>
                    </Box>
                  );
                })()}

              </Box>
            )}


            {/* Reformation Review Section - Only show when status is "submitted" */}
            {formStatus === "submitted" && (
              <ReformationReviewSection section={section} />
            )}

            {/* Trader Question - Show between consecutive sections dynamically */}
            {(() => {
              // Get the next section for trader question (exclude trader sections from this calculation)
              const mainSections = visibleSections.filter(s => s.sectionType !== 'Trader');
              const currentMainIndex = mainSections.findIndex(s => s.uniqueKey === section.uniqueKey);
              const nextSection = mainSections[currentMainIndex + 1];
              
              // Define section types that should not have trader questions after them
              const excludedSectionTypes = ['Trader', 'Additional', 'Review'];
              const excludedSectionTitles = ['Additional Questions', 'Reformation Review'];
              
              // Show trader question if:
              // 1. There is a next section
              // 2. Current section is not in excluded types
              // 3. Current section title is not in excluded titles
              // 4. Next section is not in excluded types
              // 5. Next section title is not in excluded titles
              const shouldShowTraderQuestion = nextSection && 
                !excludedSectionTypes.includes(section.sectionType) &&
                !excludedSectionTitles.includes(section.title) &&
                !excludedSectionTypes.includes(nextSection.sectionType) &&
                !excludedSectionTitles.includes(nextSection.title);

              if (!shouldShowTraderQuestion) return null;

              return (
                <Box sx={{ mt: 4 }}>
                  <TraderQuestion
                    firstSection={section.title}
                    secondSection={nextSection.title}
                    onAddTrader={handleAddTrader}
                    onRemoveTrader={handleRemoveTrader}
                    disabled={isSectionApproved}
                    hasTrader={(() => {
                      // Check if trader section exists in form structure
                      if (formConfig && formConfig.sections) {
                        return Object.values(formConfig.sections).some(formSection => {
                          if (formSection.sectionType !== 'Trader') return false;
                          
                          // Check if this trader section is between the current section and next section
                          const expectedSectionId = `trader_${section.title.toLowerCase()}_${nextSection.title.toLowerCase()}`.replace(/\s+/g, '_');
                          const actualSectionId = formSection.sectionId;
                          
                          return actualSectionId === expectedSectionId;
                        });
                      }
                      return false;
                    })()}
                  />
                </Box>
              );
            })()}
          </Box>
        );
      });
      })()}


      {/* Submit Button */}
      <Box
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Custom Popover Box */}
        {showTooltip && (
          <Box
            sx={{
              backgroundColor: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "6px",
              p: 2,
              maxWidth: "300px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              animation: "fadeIn 0.2s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "translateY(10px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {formStatus === "submitted" ? (
              <Typography
                variant="body2"
                sx={{ color: "#92400e", fontSize: "12px", fontWeight: 500 }}
              >
                Your changes have been auto-saved and shared with the brand.
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: "#92400e", fontSize: "12px", fontWeight: 500 }}
                >
                  📋 To submit this form:
                </Typography>
                {incompleteSections.length > 0 ? (
                  <Box sx={{ color: "#92400e", fontSize: "11px", mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: "#92400e", fontSize: "11px" }}>
                      Complete the following sections or submit them with notes:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {incompleteSections.map((section) => (
                        <Box component="li" key={section} sx={{ lineHeight: 1.4 }}>
                          <Typography variant="body2" sx={{ color: "#92400e", fontSize: "11px" }}>
                            {section}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "#92400e", fontSize: "11px", mt: 0.5 }}
                  >
                    {getSubmitTooltipMessage()}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={() => {
            // console.log("[JsonFormRenderer] Submit button clicked!");
            handleFormSubmit();
          }}
          disabled={
            formStatus === "submitted" ||
            !allSectionsComplete ||
            isAnySectionSaving ||
            submissionState.isSubmitting
          }
          sx={{
            backgroundColor: formStatus === "submitted" 
              ? "#d1d5db" 
              : allSectionsComplete ? "#2D3282" : "#d1d5db",
            color: formStatus === "submitted" ? "#9ca3af" : "#ffffff",
            fontWeight: 500,
            fontSize: "14px",
            padding: "10px 16px",
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: formStatus === "submitted" 
                ? "#d1d5db"
                : allSectionsComplete ? "#1E1F5C" : "#d1d5db",
            },
            "&:disabled": {
              backgroundColor: "#d1d5db",
              color: "#9ca3af",
            },
          }}
        >
          {formStatus === "submitted"
            ? // When form is already submitted
              isAnySectionSaving
              ? "Autosaving..."
              : !allSectionsComplete
              ? "Hover over 'Check Incomplete Fields' to see missing fields"
              : lastAutoSaveTime > 0
              ? `Your changes have been auto-saved at ${new Date(lastAutoSaveTime).toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit', 
                  hour12: true 
                })}`
              : "Form submitted"
            : // When form is not yet submitted
              submissionState.isSubmitting
              ? "Submitting..."
              : isAnySectionSaving
              ? "Autosaving..."
              : !allSectionsComplete
              ? "Submit Form | Hover over 'Check Incomplete Fields' to see missing fields"
              : "Submit Form"}
        </Button>
      </Box>


      <Modal open={open} onClose={handleClose}>
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 1000,
            p: 4,
            boxShadow:
              "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1)",
            border: "1px solid rgba(234, 236, 240, 1)",
            borderRadius: "12px",
            py: 2,
          }}
        >
          <AuditDocumentDrop
            organisationId={params.organisationId}
            supplierId={params.supplierId}
            factoryId={params.factoryId}
            onClose={handleClose}
            mode="traceability_form"
            uploadContext={uploadContext}
          />
        </Card>
      </Modal>

      <FAQDrawer
        open={openFAQDrawer}
        onClose={() => handleOpenCloseFAQDrawer(false)}
        sectionTitle={currentSectionTitle}
      />
    </Box>
  );
};

export default JsonFormRenderer;
